import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Banner {
  id: string;
  image_desktop: string | null;
  image_mobile: string | null;
  title: string | null;
  subtitle: string | null;
  cta_text: string | null;
  cta_link: string | null;
  is_active: boolean;
  display_order: number;
  start_date: string | null;
  end_date: string | null;
}

const empty: Partial<Banner> = {
  title: "", subtitle: "", cta_text: "", cta_link: "",
  image_desktop: "", image_mobile: "",
  is_active: true, display_order: 0, start_date: null, end_date: null,
};

const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

const AdminBanners = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Banner>>(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<"desktop" | "mobile" | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("banners")
      .select("*")
      .order("display_order", { ascending: true });
    if (error) toast.error(error.message);
    setBanners((data as Banner[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ ...empty, display_order: (banners.at(-1)?.display_order ?? 0) + 1 });
    setOpen(true);
  };
  const openEdit = (b: Banner) => { setEditing(b); setOpen(true); };

  const handleUpload = async (file: File, kind: "desktop" | "mobile") => {
    try {
      setUploading(kind);
      const ext = file.name.split(".").pop();
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("banners").upload(path, file, {
        cacheControl: "3600", upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("banners").getPublicUrl(path);
      setEditing((prev) => ({
        ...prev,
        [kind === "desktop" ? "image_desktop" : "image_mobile"]: data.publicUrl,
      }));
      toast.success("Image uploaded");
    } catch (e: any) {
      toast.error(e.message ?? "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      image_desktop: editing.image_desktop || null,
      image_mobile: editing.image_mobile || null,
      title: editing.title || null,
      subtitle: editing.subtitle || null,
      cta_text: editing.cta_text || null,
      cta_link: editing.cta_link || null,
      is_active: !!editing.is_active,
      display_order: Number(editing.display_order ?? 0),
      start_date: editing.start_date || null,
      end_date: editing.end_date || null,
    };
    const { error } = editing.id
      ? await supabase.from("banners").update(payload).eq("id", editing.id)
      : await supabase.from("banners").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(editing.id ? "Banner updated" : "Banner created");
    setOpen(false);
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("banners").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Banner deleted");
    setDeleteId(null);
    load();
  };

  const reorder = async (b: Banner, dir: -1 | 1) => {
    const idx = banners.findIndex((x) => x.id === b.id);
    const swap = banners[idx + dir];
    if (!swap) return;
    await Promise.all([
      supabase.from("banners").update({ display_order: swap.display_order }).eq("id", b.id),
      supabase.from("banners").update({ display_order: b.display_order }).eq("id", swap.id),
    ]);
    load();
  };

  const toggleActive = async (b: Banner) => {
    await supabase.from("banners").update({ is_active: !b.is_active }).eq("id", b.id);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold">Banners</h1>
          <p className="text-sm text-muted-foreground">Manage the homepage hero slider</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4" /> New banner</Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : banners.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
          No banners yet. Create your first one.
        </Card>
      ) : (
        <div className="grid gap-4">
          {banners.map((b, i) => (
            <Card key={b.id} className="overflow-hidden">
              <div className="flex flex-col md:flex-row gap-4 p-4">
                <div className="w-full md:w-64 aspect-[16/6] shrink-0 rounded-md bg-muted overflow-hidden">
                  {b.image_desktop || b.image_mobile ? (
                    <img src={b.image_desktop ?? b.image_mobile ?? ""} alt={b.title ?? ""} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{b.title || "Untitled banner"}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${b.is_active ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
                          {b.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      {b.subtitle && <p className="text-sm text-muted-foreground line-clamp-1">{b.subtitle}</p>}
                      <div className="text-xs text-muted-foreground mt-1">
                        Order #{b.display_order}
                        {b.cta_link && <> · <span className="truncate">{b.cta_link}</span></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch checked={b.is_active} onCheckedChange={() => toggleActive(b)} />
                      <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => reorder(b, -1)}><ArrowUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" disabled={i === banners.length - 1} onClick={() => reorder(b, 1)}><ArrowDown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(b)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(b.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing.id ? "Edit banner" : "New banner"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <ImageUploadField
                label="Desktop image"
                hint="Recommended: 1920 × 600 px (ratio 16:5) — keep key subjects in the center safe area."
                aspectClass="aspect-[16/5]"
                url={editing.image_desktop ?? ""}
                uploading={uploading === "desktop"}
                onFile={(f) => handleUpload(f, "desktop")}
                onClear={() => setEditing((p) => ({ ...p, image_desktop: "" }))}
              />
              <ImageUploadField
                label="Mobile image (optional)"
                hint="Recommended: 920 × 400 px (ratio 2.3:1) — main content centered, edges may crop."
                aspectClass="aspect-[23/10]"
                url={editing.image_mobile ?? ""}
                uploading={uploading === "mobile"}
                onFile={(f) => handleUpload(f, "mobile")}
                onClear={() => setEditing((p) => ({ ...p, image_mobile: "" }))}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Headline</Label>
                <Input value={editing.title ?? ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Eid Special Collection" />
              </div>
              <div>
                <Label>Small label</Label>
                <Input value={editing.subtitle ?? ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} placeholder="New arrivals" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>CTA text</Label>
                <Input value={editing.cta_text ?? ""} onChange={(e) => setEditing({ ...editing, cta_text: e.target.value })} placeholder="Shop now" />
              </div>
              <div>
                <Label>CTA link</Label>
                <Input value={editing.cta_link ?? ""} onChange={(e) => setEditing({ ...editing, cta_link: e.target.value })} placeholder="/?cat=Saree" />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Display order</Label>
                <Input type="number" value={editing.display_order ?? 0} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Start date</Label>
                <Input type="datetime-local" value={toLocalInput(editing.start_date ?? null)}
                  onChange={(e) => setEditing({ ...editing, start_date: e.target.value ? new Date(e.target.value).toISOString() : null })} />
              </div>
              <div>
                <Label>End date</Label>
                <Input type="datetime-local" value={toLocalInput(editing.end_date ?? null)}
                  onChange={(e) => setEditing({ ...editing, end_date: e.target.value ? new Date(e.target.value).toISOString() : null })} />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={!!editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing.id ? "Save changes" : "Create banner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId && remove(deleteId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ImageUploadField = ({ label, hint, aspectClass = "aspect-[16/6]", url, uploading, onFile, onClear }: {
  label: string; hint?: string; aspectClass?: string; url: string; uploading: boolean;
  onFile: (f: File) => void; onClear: () => void;
}) => (
  <div>
    <Label>{label}</Label>
    {hint && <p className="text-xs text-muted-foreground mt-0.5 mb-1">{hint}</p>}
    <div className={`mt-1 rounded-md border border-dashed bg-muted/30 ${aspectClass} relative overflow-hidden flex items-center justify-center`}>
      {url ? (
        <>
          <img src={url} alt="" className="h-full w-full object-cover" />
          <Button type="button" size="sm" variant="secondary" className="absolute top-2 right-2 h-7" onClick={onClear}>Remove</Button>
        </>
      ) : uploading ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : (
        <label className="cursor-pointer text-xs text-muted-foreground flex flex-col items-center gap-1 px-3 text-center">
          <ImageIcon className="h-5 w-5" />
          <span>Click to upload</span>
          <input type="file" accept="image/*" className="hidden"
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </label>
      )}
    </div>
  </div>
);

export default AdminBanners;
